import React, { useContext, useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../../../context/CartContext';
import {
  Printer, X, CheckCircle2, Smartphone, Landmark, Receipt, Split, Check,
  CreditCard, Banknote, ShoppingBag, Tag, Percent, BadgePercent, ChevronDown, IndianRupee,
  User, Phone
} from 'lucide-react';
import { billService } from '../../../services/billService';
import { toast } from 'react-hot-toast';
import { usePaymentAccounts } from "../../../hooks/usePaymentAccounts";
import { useKOT } from '../../../hooks/useKOT';
import { useMenuItems } from '../../../hooks/useMenuItems';
import { DEFAULT_PRINTER_CONFIG, PRINT_SETTINGS } from '../../../config/printerConfig';
import printService from '../../../services/printService';
import receiptConfigurationService from '../../../services/receiptConfigurationService';
import branchService from '../../../services/branchService';
import { generateBillContent, DEFAULT_RECEIPT_CONFIG } from '../../../utils/receiptFormatter';
import { useDiscountPOS, calculateDiscountAmount } from '../../../hooks/useDiscountPOS';
import { customerService } from '../../../services/customerService';
import DiscountSelectionModal from './DiscountSelectionModal';
import AppliedDiscountBadge from './AppliedDiscountBadge';

const NC_REASONS = [
  { code: 'STAFF_MEAL', label: 'Staff Meal' },
  { code: 'OWNER_CONSUMPTION', label: 'Owner Consumption' },
  { code: 'GUEST_COMPLAINT', label: 'Guest Complaint' },
  { code: 'FOOD_QUALITY', label: 'Food Quality Issue' },
  { code: 'WRONG_ORDER', label: 'Wrong Order' },
  { code: 'BIRTHDAY', label: 'Birthday/Anniversary' },
  { code: 'PR_MARKETING', label: 'PR / Marketing' },
  { code: 'VIP_GUEST', label: 'VIP Guest' },
  { code: 'TASTING', label: 'Menu Tasting' },
  { code: 'MGMT_DISCRETION', label: 'Management Discretion' },
];

export default function ReceiptDisplay({ onClose, isCounterMode = false, onNewOrder, canApplyPromo = true, canApplyDiscount = true }) {
  const navigate = useNavigate();
  const { calculateTotal, getCurrentOrder, finalizeOrder, currentTableId, currenttableSplit, linkedCustomer, linkedWaiter, clearLinkedCustomerAndWaiter } = useContext(CartContext);
  const receiptRef = useRef(null);
  const { accounts: paymentAccounts, loading: accountsLoading } = usePaymentAccounts();

  // Get all items from all active KOTs for this split
  const tableSplit = currenttableSplit || 'A';
  const { activeOrders, loading: ordersLoading } = useKOT(
    currentTableId,
    tableSplit,
    { skipIfNew: false }
  );

  // Menu items carry the effective tax config (rate, CGST/SGST split, inclusive flag)
  // resolved server-side as item override → category → branch default. The order items
  // returned by the KOT API do not carry tax at all, so this lookup is the only way to
  // get it — same as PrintReceiptModal does for the dine-in print-bill step.
  const { items: menuItems } = useMenuItems();
  const menuItemsMap = useMemo(() => {
    const map = {};
    (menuItems || []).forEach(item => { map[item.id] = item; });
    return map;
  }, [menuItems]);

  // Extract all items from all KOTs, with tax attached from the menu item
  const allKOTItems = useMemo(() => (activeOrders || []).flatMap(kot =>
    (kot.items || []).map(item => {
      const menuItem = menuItemsMap[item.menuItemId];

      return {
        cartId: `${kot.kotNumber}-${item.id}`,
        id: item.id,
        menuItemId: item.menuItemId,
        name: item.itemName || item.name,
        price: item.unitPrice || item.price || 0,
        finalPrice: item.unitPrice || item.price || 0,
        quantity: item.quantity || 1,
        variantId: item.variantId,
        variantName: item.variantName,
        // Tax information from menu item's effective tax
        taxPercentage: menuItem?.effectiveTaxRate || 0,
        cgstRate: menuItem?.effectiveTaxCgstRate || 0,
        sgstRate: menuItem?.effectiveTaxSgstRate || 0,
        inclusiveTax: menuItem?.effectiveInclusiveTaxRate !== false,
        // NC status from existing bill items (read-only in settlement)
        isComp: item.isComp || false,
        compReason: item.compReason || null
      };
    })
  ), [activeOrders, menuItemsMap]);

  // Extract all order IDs from active KOTs for bill creation
  const orderIds = (activeOrders || [])
    .map(order => order.id)
    .filter(id => id != null);

  const [paymentMode, setPaymentMode] = useState('CASH');
  const [cashReceived, setCashReceived] = useState(0);
  const [cashPart, setCashPart] = useState(0);
  const [onlinePart, setOnlinePart] = useState(0);
  const [selectedUpiAccountId, setSelectedUpiAccountId] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // NC (Non-Chargeable) state — read-only from existing bill (NC is applied in PrintReceiptModal)
  const [isNcBill, setIsNcBill] = useState(false);
  const [ncReason, setNcReason] = useState('');

  // Counter Mode: Receipt delivery options (PRINT / EBILL / BOTH)
  const [receiptDeliveryMethod, setReceiptDeliveryMethod] = useState('PRINT');
  const [whatsappNumber, setWhatsappNumber] = useState('');

  // Get customer details from linked customer (for auto-fill in e-Bill)
  const customerPhone = linkedCustomer?.phone || linkedCustomer?.mobileNumber || linkedCustomer?.mobile || '';
  const customerName = linkedCustomer?.name || linkedCustomer?.customerName || '';

  // Counter Mode: Discount state (only used when no discount was applied from PrintReceipt)
const [discountMode, setDiscountMode] = useState(() =>
  canApplyPromo ? 'code' : 'manual'
);
  const [discountType, setDiscountType] = useState('PERCENTAGE');
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountFixedAmount, setDiscountFixedAmount] = useState(0);
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  // Discount POS hook for code-based discounts
  const {
    suggestions: discountSuggestions,
    appliedDiscount,
    loading: discountsLoading,
    validating: codeValidating,
    fetchSuggestions,
    validateCode,
    applyDiscount,
    removeDiscount,
    reset: resetDiscount,
  } = useDiscountPOS();

  // Receipt configuration state
  const [receiptConfig, setReceiptConfig] = useState(DEFAULT_RECEIPT_CONFIG);
  const [branchDetails, setBranchDetails] = useState(null);

  // Existing bill state (for settling payment_pending bills)
  const [existingBill, setExistingBill] = useState(null);
  const [allPendingBills, setAllPendingBills] = useState([]); // for split bills
  const [activeCheckIndex, setActiveCheckIndex] = useState(0); // which check is being settled
  const [settledChecks, setSettledChecks] = useState({}); // { billId: true }
  const [loadingBill, setLoadingBill] = useState(true);

  // Loyalty redemption
  const [loyaltyBalance, setLoyaltyBalance] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [redeemApplied, setRedeemApplied] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);

  // Generate receipt number once
  const [receiptNumber] = useState(() => `ORD-${Date.now()}`);

  const currentOrder = getCurrentOrder();

  // Display name — replace MAVERICK_COUNTER with Takeaway
  const displayTableName = currentOrder?.tableName?.toUpperCase().includes('MAVERICK_COUNTER')
    ? 'Takeaway'
    : currentOrder?.tableName || 'N/A';

  // Build a lookup of NC status from existing bill items
  const existingBillItemNcMap = {};
  if (existingBill?.billItems) {
    existingBill.billItems.forEach(bi => {
      if (bi.isComp) existingBillItemNcMap[bi.itemName] = { isComp: true, compReason: bi.compReason };
    });
  }

  // Group items by name for bill display
  // For split bills: use existingBill.billItems (check-specific items)
  // For normal bills: use allKOTItems (from active orders)
  const isSplitBillMode = allPendingBills.length > 1;
  const sourceItems = isSplitBillMode && existingBill?.billItems?.length > 0
    ? existingBill.billItems.map(bi => ({
        name: bi.itemName || bi.name,
        quantity: bi.quantity || 1,
        price: bi.unitPrice || bi.price || 0,
        total: bi.totalAmount || (bi.unitPrice || 0) * (bi.quantity || 1),
        isComp: bi.isComp || false,
        compReason: bi.compReason || null,
      }))
    : allKOTItems.map(item => ({
        name: item.name,
        quantity: item.quantity || 1,
        price: item.price || 0,
        total: (item.price || 0) * (item.quantity || 1),
        isComp: existingBillItemNcMap[item.name]?.isComp || false,
        compReason: existingBillItemNcMap[item.name]?.compReason || null,
      }));

  const groupedItems = sourceItems.reduce((acc, item) => {
    const key = item.name;
    if (!acc[key]) {
      acc[key] = { name: key, quantity: 0, price: item.price, total: 0, isComp: item.isComp, compReason: item.compReason };
    }
    acc[key].quantity += item.quantity;
    acc[key].total += item.total;
    return acc;
  }, {});

  const billItems = Object.values(groupedItems);
  const totalItemsCount = billItems.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate CGST/SGST from items if not available from existing bill
  // Get average tax rates from items (weighted by price * quantity)
  const calculateAverageTaxRates = () => {
    if (allKOTItems.length === 0) return { cgstRate: 0, sgstRate: 0, taxPercentage: 0, isInclusive: true };

    let totalValue = 0;
    let weightedCgst = 0;
    let weightedSgst = 0;

    allKOTItems.forEach(item => {
      const itemValue = (item.finalPrice || item.price) * item.quantity;
      totalValue += itemValue;
      weightedCgst += (item.cgstRate || 0) * itemValue;
      weightedSgst += (item.sgstRate || 0) * itemValue;
    });

    const avgCgst = totalValue > 0 ? weightedCgst / totalValue : 0;
    const avgSgst = totalValue > 0 ? weightedSgst / totalValue : 0;

    // Check if tax is inclusive (use first item's setting, default to true)
    const isInclusive = allKOTItems[0]?.inclusiveTax !== false;

    return {
      cgstRate: avgCgst,
      sgstRate: avgSgst,
      taxPercentage: avgCgst + avgSgst,
      isInclusive
    };
  };

  const calculatedTaxRates = calculateAverageTaxRates();

  // Tax configuration - use existing bill rates if available, otherwise from items
  const TAX_PERCENTAGE = existingBill?.taxPercentage ?? calculatedTaxRates.taxPercentage;
  const CGST_RATE = existingBill?.cgstRate ?? calculatedTaxRates.cgstRate;
  const SGST_RATE = existingBill?.sgstRate ?? calculatedTaxRates.sgstRate;
  const IS_INCLUSIVE_TAX = existingBill?.inclusiveTaxRate ?? calculatedTaxRates.isInclusive;

  // Calculate totals - use existing bill data if available, otherwise calculate from items
  const subtotal = existingBill?.subtotal ?? allKOTItems.reduce((total, item) => {
    const price = item.finalPrice || item.price;
    return total + (price * item.quantity);
  }, 0);

  // NC amount - use stored value from existing bill, otherwise 0 (NC is applied in PrintReceiptModal)
  const ncAmount = existingBill?.ncAmount ?? 0;

  // Effective subtotal after NC deduction (used for all further calculations)
  const chargeableSubtotal = subtotal - ncAmount;

  // Check if discount was already applied from PrintReceipt (existingBill)
  const hasExistingDiscount = existingBill?.discountAmount > 0;

  // Calculate local discount for Counter mode (when no existing discount)
 const localDiscountAmount = (() => {
  if (!isCounterMode || hasExistingDiscount) return 0;
  if (discountMode === 'code' && appliedDiscount && canApplyPromo) {
    return calculateDiscountAmount(appliedDiscount, allKOTItems.map(item => ({
      menuItemId: item.menuItemId,
      categoryId: item.categoryId,
      price: item.finalPrice || item.price,
      quantity: item.quantity
    })), chargeableSubtotal);
  } else if (discountMode === 'manual' && canApplyDiscount) {
    return discountType === 'PERCENTAGE'
      ? (chargeableSubtotal * discountPercentage) / 100
      : Math.min(discountFixedAmount, chargeableSubtotal);
  }
  return 0;
})();

  // Use discount from existing bill if available, otherwise use local discount
  const discountAmount = hasExistingDiscount ? existingBill.discountAmount : localDiscountAmount;
  const effectiveDiscountPercentage = hasExistingDiscount
    ? (existingBill?.discountPercentage ?? 0)
    : (discountMode === 'manual' && discountType === 'PERCENTAGE' ? discountPercentage : (chargeableSubtotal > 0 ? (discountAmount / chargeableSubtotal) * 100 : 0));
  const discountCode = hasExistingDiscount ? (existingBill?.discountCode || null) : (appliedDiscount?.code || null);
  const discountReason = hasExistingDiscount
  ? (existingBill?.discountReason || null)
  : (appliedDiscount && canApplyPromo
      ? `Code: ${appliedDiscount.code}`
      : (discountAmount > 0 && canApplyDiscount ? 'Manual discount' : null));

  // Effective discount type for printing - derive from existing bill or local state
  const effectiveDiscountType = hasExistingDiscount
    ? (existingBill?.discountType || (existingBill?.discountPercentage > 0 ? 'PERCENTAGE' : 'FIXED'))
    : (appliedDiscount?.discountType?.toUpperCase() || discountType);

  // Tax amounts, computed the same way as the dine-in print-bill step
  // (PrintReceiptModal). Inclusive tax is already inside the menu price, so it is
  // EXTRACTED and the total is unchanged; exclusive tax is ADDED on top.
  // Only reached in counter mode — dine-in and split bills read the stored values
  // off the payment_pending bill via the existingBill guards below.
  const amountAfterDiscount = chargeableSubtotal - discountAmount;

  let computedTaxableAmount, computedTaxAmount, computedCgstAmount, computedSgstAmount, computedTotalAmount;

  if (IS_INCLUSIVE_TAX) {
    computedTaxAmount = TAX_PERCENTAGE > 0
      ? (amountAfterDiscount * TAX_PERCENTAGE) / (100 + TAX_PERCENTAGE)
      : 0;
    computedTaxableAmount = amountAfterDiscount - computedTaxAmount;
    computedCgstAmount = CGST_RATE > 0 ? (amountAfterDiscount * CGST_RATE) / (100 + TAX_PERCENTAGE) : 0;
    computedSgstAmount = SGST_RATE > 0 ? (amountAfterDiscount * SGST_RATE) / (100 + TAX_PERCENTAGE) : 0;
    computedTotalAmount = amountAfterDiscount;
  } else {
    computedTaxableAmount = amountAfterDiscount;
    computedCgstAmount = (computedTaxableAmount * CGST_RATE) / 100;
    computedSgstAmount = (computedTaxableAmount * SGST_RATE) / 100;
    computedTaxAmount = computedCgstAmount + computedSgstAmount;
    computedTotalAmount = computedTaxableAmount + computedTaxAmount;
  }

  const taxableAmount = existingBill?.taxableAmount ?? computedTaxableAmount;
  const taxAmount = existingBill?.taxAmount ?? computedTaxAmount;
  const cgstAmount = existingBill?.cgstAmount ?? computedCgstAmount;
  const sgstAmount = existingBill?.sgstAmount ?? computedSgstAmount;
  const totalAmount = existingBill?.totalAmount ?? computedTotalAmount;
  const preLoyaltyFinalAmount  = isNcBill ? 0 : (existingBill?.finalAmount ?? Math.round(totalAmount));
  const roundOff = existingBill?.roundOff ?? (preLoyaltyFinalAmount - totalAmount);
 
  // Loyalty redemption amount (post-tax deduction)
  const loyaltyDiscountAmount = redeemApplied && loyaltyBalance
    ? Math.round(redeemPoints * (loyaltyBalance.redeemRate || 1) * 100) / 100
    : 0;
  const finalAmount = Math.max(0, preLoyaltyFinalAmount - loyaltyDiscountAmount);
  const payableAmount = finalAmount;

  const changeDue = Math.max(0, (cashReceived || 0) - finalAmount);

  const upiAccounts = paymentAccounts.filter(
    acc => (acc.account_type === "upi" || acc.accountType === "upi") && (acc.is_active || acc.isActive)
  );
  const selectedUpiAccount = upiAccounts.find((acc) => acc.id === selectedUpiAccountId);

  // Auto-select default UPI account when accounts load
  useEffect(() => {
    if (!accountsLoading && upiAccounts.length > 0) {
      const defaultAccount = upiAccounts.find(acc => acc.isDefault === true || acc.is_default === true);
      if (defaultAccount) {
        setSelectedUpiAccountId(defaultAccount.id);
      } else if (!selectedUpiAccountId) {
        setSelectedUpiAccountId(upiAccounts[0].id);
      }
    }
  }, [accountsLoading, upiAccounts.length]);

  useEffect(() => {
  if (!canApplyPromo && appliedDiscount) {
    removeDiscount();
  }
}, [canApplyPromo]);

  // Auto-fill payment amount based on payment mode (use payableAmount for loyalty-adjusted amount)
  useEffect(() => {
    if (payableAmount > 0) {
      if (paymentMode === 'CASH') {
        setCashReceived(payableAmount);
      } else if (paymentMode === 'SPLIT') {
        const half = Math.round(payableAmount / 2);
        setCashPart(half);
        setOnlinePart(payableAmount - half);
      }
    }
  }, [payableAmount, paymentMode]);

  // Auto-fill WhatsApp number from linked customer when e-Bill is selected (Counter mode)
  useEffect(() => {
    if (isCounterMode && (receiptDeliveryMethod === 'EBILL' || receiptDeliveryMethod === 'BOTH')) {
      if (customerPhone && !whatsappNumber) {
        // Clean the phone number (remove non-digits) and take last 10 digits
        const cleaned = customerPhone.replace(/\D/g, '');
        const last10 = cleaned.length > 10 ? cleaned.slice(-10) : cleaned;
        setWhatsappNumber(last10);
      }
    }
  }, [isCounterMode, receiptDeliveryMethod, customerPhone]);

  // Fetch existing pending bills for this table/split
  useEffect(() => {
    const fetchExistingBills = async () => {
      if (!currentTableId || !tableSplit || isCounterMode) {
        setLoadingBill(false);
        return;
      }

      try {
        // Fetch ALL pending bills (handles split bills)
        let pendingBills = [];
        try {
          pendingBills = await billService.getAllPendingBills(currentTableId, tableSplit);
        } catch {
          // Fallback to single bill fetch if new endpoint not available
          const single = await billService.getPendingBillByTableSplit(currentTableId, tableSplit);
          if (single) pendingBills = [single];
        }

        if (pendingBills && pendingBills.length > 0) {
          if (pendingBills.length === 1) {
            // Single bill — normal flow
            console.log('Found 1 pending bill:', pendingBills[0]?.billNumber);
            setExistingBill(pendingBills[0]);
          } else {
            // Multiple bills — split bill flow
            console.log(`Found ${pendingBills.length} split bills:`, pendingBills.map(b => b.billNumber));
            setAllPendingBills(pendingBills);
            setExistingBill(pendingBills[0]);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch existing bills:', error);
      } finally {
        setLoadingBill(false);
      }
    };

    fetchExistingBills();
  }, [currentTableId, tableSplit, isCounterMode]);

  // Initialize NC state from existing bill (read-only from PrintReceiptModal)
  useEffect(() => {
    if (existingBill?.isNc) {
      setIsNcBill(true);
      setNcReason(existingBill.ncReason || '');
    }
  }, [existingBill]);

  // Fetch loyalty balance when customer is linked
  useEffect(() => {
    if (linkedCustomer?.id) {
      customerService.getLoyaltyBalance(linkedCustomer.id).then(data => {
        if (data && data.programEnabled) {
          setLoyaltyBalance(data);
        }
      });
    }
  }, [linkedCustomer]);

  // Fetch receipt configuration and branch details
  useEffect(() => {
    const fetchReceiptConfig = async () => {
      try {
        const config = await receiptConfigurationService.getDefault('BILL');
        if (config) setReceiptConfig(config);
      } catch (error) {
        console.warn('Failed to fetch receipt configuration:', error);
      }
    };

    const fetchBranchDetails = async () => {
      try {
        const branch = await branchService.getCurrentBranch();
        if (branch) setBranchDetails(branchService.formatBranchForReceipt(branch));
      } catch (error) {
        console.warn('Failed to fetch branch details:', error);
      }
    };

    fetchReceiptConfig();
    fetchBranchDetails();
  }, []);

  const timestamp = new Date().toLocaleString('en-IN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  const PRINTER_CONFIG = DEFAULT_PRINTER_CONFIG;

  // Browser print fallback.
  // `invoiceNumber` is the bill_number assigned by the backend; the local
  // `receiptNumber` placeholder is only a pre-settlement stand-in.
  const handleThermalPrint = (invoiceNumber) => {
    const displayBillNumber = invoiceNumber || receiptNumber;
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site.');
      return;
    }

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Receipt - ${displayBillNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @media screen {
              body { font-family: 'Courier New', monospace; background: #f5f5f5; padding: 20px; display: flex; justify-content: center; }
              .receipt-container { background: white; width: ${PRINTER_CONFIG.width}; padding: ${PRINTER_CONFIG.padding}; box-shadow: 0 2px 10px rgba(0,0,0,0.1); border: 1px solid #ddd; }
            }
            @media print {
              @page { size: ${PRINTER_CONFIG.width} auto; margin: 0; }
              body { margin: 0; padding: 0; width: ${PRINTER_CONFIG.width}; }
              .receipt-container { width: 100%; padding: ${PRINTER_CONFIG.padding}; }
            }
            .receipt-container { font-family: 'Courier New', monospace; font-size: ${PRINTER_CONFIG.fontSize}; line-height: ${PRINTER_CONFIG.lineHeight}; color: #000; }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .text-xs { font-size: 0.8em; }
            .header { text-align: center; margin-bottom: 1em; padding-bottom: 1em; border-bottom: 2px dashed #000; }
            .company-name { font-size: 1.4em; font-weight: bold; margin-bottom: 0.25em; }
            .info-section { margin-bottom: 1em; padding-bottom: 0.75em; border-bottom: 1px solid #000; }
            .info-row { display: flex; justify-content: space-between; margin-bottom: 0.25em; font-size: 0.9em; }
            .items-header { display: flex; justify-content: space-between; font-weight: bold; margin-bottom: 0.5em; padding-bottom: 0.5em; border-bottom: 1px solid #000; font-size: 0.9em; }
            .item-row { margin-bottom: 0.75em; }
            .item-main { display: flex; justify-content: space-between; margin-bottom: 0.2em; }
            .item-price { font-size: 0.8em; color: #333; margin-left: 0.5em; }
            .totals-section { border-top: 1px solid #000; padding-top: 0.75em; margin-top: 1em; }
            .total-row { display: flex; justify-content: space-between; margin-bottom: 0.4em; font-size: 0.9em; }
            .final-total { border-top: 2px dashed #000; padding-top: 0.75em; margin-top: 0.75em; }
            .final-total-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 1.1em; padding: 0.5em; background: #f0f0f0; }
            .payment-info { background: #f9f9f9; border: 1px solid #000; padding: 0.5em; margin: 0.75em 0; }
            .footer { text-align: center; margin-top: 1.5em; padding-top: 1em; border-top: 2px dashed #000; font-size: 0.8em; }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              ${receiptConfig.showRestaurantName ? `<div class="company-name">${branchDetails?.name || 'RESTAURANT'}</div>` : ''}
              ${receiptConfig.showAddress && branchDetails?.address ? `<div class="text-xs">${branchDetails.address}</div>` : ''}
              ${receiptConfig.showPhone && branchDetails?.phone ? `<div class="text-xs">Ph: ${branchDetails.phone}</div>` : ''}
              <div class="text-xs" style="margin-top: 0.5em;">Receipt #${displayBillNumber}</div>
            </div>
            <div class="info-section">
              <div class="info-row"><span class="font-bold">${currentOrder.tableType === 'TABLE' ? 'Table' : 'Parcel'}:</span><span class="font-bold">${displayTableName}</span></div>
              <div class="info-row text-xs"><span>Date/Time:</span><span>${timestamp}</span></div>
            </div>
            <div class="items-header"><span>Item</span><span>Qty</span><span>Amount</span></div>
            ${billItems.map(item => `
              <div class="item-row">
                <div class="item-main"><span>${item.name}</span><span>${item.quantity}</span><span>₹${item.total.toFixed(2)}</span></div>
                <div class="item-price">@ ₹${item.price.toFixed(2)}</div>
              </div>
            `).join('')}
            <div class="totals-section">
              <div class="total-row"><span>Subtotal:</span><span>₹${subtotal.toFixed(2)}</span></div>
              ${discountAmount > 0 ? `<div class="total-row"><span>Discount:</span><span>- ₹${discountAmount.toFixed(2)}</span></div>` : ''}
              ${(CGST_RATE > 0 || SGST_RATE > 0) ? `
              <div class="total-row"><span>Taxable Amount:</span><span>₹${taxableAmount.toFixed(2)}</span></div>
              <div class="total-row"><span>CGST (${CGST_RATE.toFixed(1)}%):</span><span>₹${cgstAmount.toFixed(2)}</span></div>
              <div class="total-row"><span>SGST (${SGST_RATE.toFixed(1)}%):</span><span>₹${sgstAmount.toFixed(2)}</span></div>
              ${IS_INCLUSIVE_TAX ? `<div class="total-row" style="font-style: italic; color: #666;">(Tax included in price)</div>` : ''}
              ` : ''}
              <div class="total-row"><span>Round Off:</span><span>${roundOff >= 0 ? '+' : ''}₹${roundOff.toFixed(2)}</span></div>
            </div>
            <div class="final-total">
              <div class="final-total-row"><span>TOTAL:</span><span>₹${finalAmount.toFixed(2)}</span></div>
            </div>
            <div class="payment-info">
              <div class="info-row font-bold"><span>Payment:</span><span>${paymentMode === 'CASH' ? 'Cash' : paymentMode === 'ONLINE' ? 'UPI' : 'Split'}</span></div>
              ${paymentMode === 'CASH' && cashReceived > 0 ? `<div class="info-row"><span>Received:</span><span>₹${cashReceived.toFixed(2)}</span></div><div class="info-row font-bold"><span>Change:</span><span>₹${changeDue.toFixed(2)}</span></div>` : ''}
            </div>
            <div class="footer">
              <div class="font-bold">${receiptConfig.thankYouText || 'Thank you! Visit Again!'}</div>
            </div>
          </div>
          <script>
            window.onload = function() { setTimeout(function() { window.print(); }, ${PRINT_SETTINGS.autoPrintDelay}); };
            ${PRINT_SETTINGS.autoCloseAfterPrint ? `window.onafterprint = function() { setTimeout(function() { window.close(); }, ${PRINT_SETTINGS.autoCloseDelay}); };` : ''}
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  // Direct print to thermal printer.
  // `invoiceNumber` is the backend-assigned bill_number — printed as-is so the
  // slip, the e-bill and the DB row all carry the same number.
  const handleDirectPrint = async (invoiceNumber) => {
    const displayBillNumber = invoiceNumber || receiptNumber;
    try {
      // 🔍 DEBUG: Log bill data being sent to print
      console.log('🔍 [handleDirectPrint] Tax values:', {
        taxAmount, taxableAmount, TAX_PERCENTAGE, CGST_RATE, SGST_RATE, cgstAmount, sgstAmount
      });

      const billData = {
        restaurantName: branchDetails?.name || 'RESTAURANT',
        address: branchDetails?.address || null,
        phone: branchDetails?.phone || null,
        gstin: branchDetails?.gstNumber || null,
        fssai: branchDetails?.fssaiNumber || null,
        logoUrl: branchDetails?.logoUrl || null,
        merchantUpiId: branchDetails?.upiId || null, // For QR code generation
        billNumber: displayBillNumber,
        receiptNumber: displayBillNumber,
        tableName: displayTableName,
        tableType: currentOrder.tableType,
        tableSplit: tableSplit,
        pax: currentOrder.pax,
        // Raw ISO — the formatter parses and localises it (a pre-localised
        // string would parse as Invalid Date)
        timestamp: new Date().toISOString(),
        orderType: isCounterMode || currentOrder.tableType !== 'TABLE' ? 'Takeaway' : 'Dine In',
        items: billItems.map(item => ({ name: item.name, quantity: item.quantity, price: item.price, total: item.total })),
        subtotal, discountAmount, discountPercentage: effectiveDiscountPercentage, discountType: effectiveDiscountType,
        taxAmount, taxPercentage: TAX_PERCENTAGE,
        taxableAmount,
        cgstRate: CGST_RATE,
        sgstRate: SGST_RATE,
        cgstAmount: cgstAmount,
        sgstAmount: sgstAmount,
        roundOff, finalAmount, paymentMode,
        cashReceived, changeDue, upiId: selectedUpiAccount?.upi_id, cashPart, onlinePart,
        waiterName: linkedWaiter?.name || null,
        customerName: linkedCustomer?.name || null,
        customerPhone: linkedCustomer?.phone || linkedCustomer?.mobileNumber || null,
        customerEmail: linkedCustomer?.email || null,
        customerAddress: linkedCustomer?.address || null,
      };

      const receiptOutput = generateBillContent(receiptConfig, billData);
      const response = await printService.testPrintTemplate(
        receiptOutput.content,
        'BILL',
        null,
        { logo: receiptOutput.logo, qrCode: receiptOutput.qrCode }
      );

      if (response?.success) {
        toast.success('Receipt printed!', { duration: 2000 });
        return true;
      } else {
        handleThermalPrint(displayBillNumber);
        return false;
      }
    } catch (error) {
      console.error('Direct print error:', error);
      handleThermalPrint(displayBillNumber);
      return false;
    }
  };

  // Calculate item-level totals including CGST/SGST.
  // Inclusive/exclusive is read per item (not from the bill-level flag), so a bill
  // mixing inclusive and exclusive items still totals each line correctly.
  const calculateItemTotals = (item) => {
    const basePrice = item.finalPrice || item.price;
    const itemSubtotal = basePrice * item.quantity;
    const itemDiscountAmount = (itemSubtotal * effectiveDiscountPercentage) / 100;
    const itemAmountAfterDiscount = itemSubtotal - itemDiscountAmount;

    // Use item-level tax rates if available, otherwise use bill-level rates
    const itemCgstRate = item.cgstRate || CGST_RATE;
    const itemSgstRate = item.sgstRate || SGST_RATE;
    const itemTaxPercentage = item.taxPercentage || TAX_PERCENTAGE;
    const itemIsInclusive = item.inclusiveTax !== false;

    let itemTaxableAmount, itemCgstAmount, itemSgstAmount, itemTaxAmount, itemTotalAmount;

    if (itemIsInclusive) {
      itemTaxAmount = itemTaxPercentage > 0
        ? (itemAmountAfterDiscount * itemTaxPercentage) / (100 + itemTaxPercentage)
        : 0;
      itemTaxableAmount = itemAmountAfterDiscount - itemTaxAmount;
      itemCgstAmount = itemCgstRate > 0 ? (itemAmountAfterDiscount * itemCgstRate) / (100 + itemTaxPercentage) : 0;
      itemSgstAmount = itemSgstRate > 0 ? (itemAmountAfterDiscount * itemSgstRate) / (100 + itemTaxPercentage) : 0;
      itemTotalAmount = itemAmountAfterDiscount;
    } else {
      itemTaxableAmount = itemAmountAfterDiscount;
      itemCgstAmount = (itemTaxableAmount * itemCgstRate) / 100;
      itemSgstAmount = (itemTaxableAmount * itemSgstRate) / 100;
      itemTaxAmount = itemCgstAmount + itemSgstAmount;
      itemTotalAmount = itemTaxableAmount + itemTaxAmount;
    }

    return {
      unitPrice: basePrice,
      discountAmount: itemDiscountAmount,
      taxableAmount: itemTaxableAmount,
      taxPercentage: itemTaxPercentage,
      cgstRate: itemCgstRate,
      sgstRate: itemSgstRate,
      cgstAmount: itemCgstAmount,
      sgstAmount: itemSgstAmount,
      taxAmount: itemTaxAmount,
      totalAmount: itemTotalAmount,
      inclusiveTax: itemIsInclusive
    };
  };

  // Prepare bill payload for API
  const prepareBillPayload = () => {
    let paymentStatus = 'pending';
    let paidAmount = 0;
    let balanceDue = payableAmount;

    // NC bill: no payment needed
    if (isNcBill && finalAmount === 0) {
      paymentStatus = 'completed';
      paidAmount = 0;
      balanceDue = 0;
    } else if (paymentMode === 'CASH') {
      if (cashReceived >= payableAmount) { paymentStatus = 'completed'; paidAmount = payableAmount; balanceDue = 0; }
      else { paymentStatus = 'partial'; paidAmount = cashReceived; balanceDue = payableAmount - cashReceived; }
    } else if (paymentMode === 'ONLINE') {
      paymentStatus = 'completed'; paidAmount = payableAmount; balanceDue = 0;
    } else if (paymentMode === 'SPLIT') {
      const totalPaid = cashPart + onlinePart;
      if (totalPaid >= payableAmount) { paymentStatus = 'completed'; paidAmount = payableAmount; balanceDue = 0; }
      else { paymentStatus = 'partial'; paidAmount = totalPaid; balanceDue = payableAmount - totalPaid; }
    }

    const billItemsPayload = allKOTItems.map(item => {
      const itemTotals = calculateItemTotals(item);
      return {
        menuItemId: item.menuItemId, variantId: item.variantId || null, itemName: item.name,
        variantName: item.variantName || null, quantity: item.quantity,
        unitPrice: parseFloat(itemTotals.unitPrice.toFixed(2)),
        discountAmount: parseFloat(itemTotals.discountAmount.toFixed(2)),
        taxPercentage: parseFloat(itemTotals.taxPercentage.toFixed(2)),
        cgstRate: parseFloat(itemTotals.cgstRate.toFixed(2)),
        sgstRate: parseFloat(itemTotals.sgstRate.toFixed(2)),
        taxableAmount: parseFloat(itemTotals.taxableAmount.toFixed(2)),
        cgstAmount: parseFloat(itemTotals.cgstAmount.toFixed(2)),
        sgstAmount: parseFloat(itemTotals.sgstAmount.toFixed(2)),
        taxAmount: parseFloat(itemTotals.taxAmount.toFixed(2)),
        totalAmount: parseFloat(itemTotals.totalAmount.toFixed(2)),
        inclusiveTaxRate: itemTotals.inclusiveTax,
        isComp: isNcBill || item.isComp || false,
        compReason: isNcBill ? ncReason : (item.compReason || null),
      };
    });

    const payments = [];
    if (paymentMode === 'CASH' && cashReceived > 0) {
      payments.push({
        paymentMode: 'cash', paymentAmount: parseFloat(Math.min(cashReceived, payableAmount).toFixed(2)),
        tenderedAmount: parseFloat(cashReceived.toFixed(2)), changeReturned: parseFloat(changeDue.toFixed(2)),
        paymentReference: null, paymentStatus: 'completed', notes: cashReceived >= payableAmount ? 'Full payment' : 'Partial'
      });
    } else if (paymentMode === 'ONLINE') {
      payments.push({
        paymentMode: 'upi', paymentAmount: parseFloat(payableAmount.toFixed(2)),
        tenderedAmount: parseFloat(payableAmount.toFixed(2)), changeReturned: 0.00,
        paymentReference: selectedUpiAccount?.id || null, paymentStatus: 'completed',
        notes: `UPI via ${selectedUpiAccount?.payment_gateway || 'UPI'}`
      });
    } else if (paymentMode === 'SPLIT') {
      if (cashPart > 0) {
        payments.push({
          paymentMode: 'cash', paymentAmount: parseFloat(cashPart.toFixed(2)),
          tenderedAmount: parseFloat(cashPart.toFixed(2)), changeReturned: 0.00,
          paymentReference: null, paymentStatus: 'completed', notes: 'Cash portion'
        });
      }
      if (onlinePart > 0) {
        payments.push({
          paymentMode: 'upi', paymentAmount: parseFloat(onlinePart.toFixed(2)),
          tenderedAmount: parseFloat(onlinePart.toFixed(2)), changeReturned: 0.00,
          paymentReference: selectedUpiAccount?.id || null, paymentStatus: 'completed',
          notes: `UPI via ${selectedUpiAccount?.payment_gateway || 'UPI'}`
        });
      }
    }

    // Determine bill delivery method based on mode
    // Counter mode: Use selected delivery method
    // Dine-in mode: Default to PRINT (receipt was already printed via PrintReceiptModal)
    const deliveryMethod = isCounterMode
      ? (receiptDeliveryMethod === 'EBILL' ? 'WHATSAPP' : receiptDeliveryMethod === 'BOTH' ? 'WHATSAPP' : 'PRINT')
      : 'PRINT';

    const billPayload = {
      tableId: currentOrder.tableId, tableSplit: tableSplit,
      customerId: linkedCustomer?.id || null, waiterId: linkedWaiter?.id || null,
      waiterName: linkedWaiter?.name || null, totalItems: allKOTItems.length,
      subtotal: parseFloat(subtotal.toFixed(2)), discountPercentage: parseFloat(effectiveDiscountPercentage.toFixed(2)),
      discountAmount: parseFloat(discountAmount.toFixed(2)), discountReason: discountReason || null,
      taxPercentage: parseFloat(TAX_PERCENTAGE.toFixed(2)),
      cgstRate: parseFloat(CGST_RATE.toFixed(2)),
      sgstRate: parseFloat(SGST_RATE.toFixed(2)),
      taxableAmount: parseFloat(taxableAmount.toFixed(2)),
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)), totalAmount: parseFloat(totalAmount.toFixed(2)),
      inclusiveTaxRate: IS_INCLUSIVE_TAX,
      roundOff: parseFloat(roundOff.toFixed(2)), finalAmount: parseFloat(finalAmount.toFixed(2)),
      paymentStatus, paidAmount: parseFloat(paidAmount.toFixed(2)), balanceDue: parseFloat(balanceDue.toFixed(2)),
      billStatus: balanceDue > 0 ? 'open' : 'closed',
      billType: existingBill?.billType || (isNcBill ? 'nc' : 'sale'),
      isNc: existingBill?.isNc || isNcBill,
      ncReason: existingBill?.ncReason || (isNcBill ? ncReason : null),
      ncAmount: parseFloat(ncAmount.toFixed(2)),
      referenceBillNumber: null,
      billDeliveryMethod: deliveryMethod,
      recipientPhone: isCounterMode && (receiptDeliveryMethod === 'EBILL' || receiptDeliveryMethod === 'BOTH') && whatsappNumber
        ? (whatsappNumber.startsWith('91') ? whatsappNumber : '91' + whatsappNumber)
        : null,
      // AFTER
notes: currentOrder.orderNotes?.billNotes || null, billItems: billItemsPayload, payments, orderIds,
loyaltyPointsRedeemed: redeemApplied && redeemPoints > 0 ? redeemPoints : null,
loyaltyDiscountAmount: redeemApplied && loyaltyDiscountAmount > 0 ? parseFloat(loyaltyDiscountAmount.toFixed(2)) : null,
    };

    // 🔍 DEBUG: Log bill payload being sent to API
    console.log('🔍 [prepareBillPayload] Bill payload tax values:', {
      taxPercentage: billPayload.taxPercentage,
      cgstRate: billPayload.cgstRate,
      sgstRate: billPayload.sgstRate,
      cgstAmount: billPayload.cgstAmount,
      sgstAmount: billPayload.sgstAmount,
      taxAmount: billPayload.taxAmount,
    });
    console.log('🔍 [prepareBillPayload] First bill item tax:', billItemsPayload[0]);

    return billPayload;
  };

  // Settle Payment (with auto-print for counter mode)
  const handleSettlePayment = async () => {
    // Skip payment validation for fully NC bills
    if (!(isNcBill && finalAmount === 0)) {
      if (paymentMode === 'CASH' && cashReceived < finalAmount) {
        toast.error('Insufficient cash received'); return;
      }
      if (paymentMode === 'SPLIT' && cashPart + onlinePart !== finalAmount) {
        toast.error('Split amounts must equal total'); return;
      }
    }

    setIsProcessing(true);
    const loadingToast = toast.loading('Processing payment...');

    try {
      // Redeem loyalty points if applied (before bill creation)
      if (redeemApplied && loyaltyBalance && linkedCustomer?.id) {
        try {
          await customerService.redeemPoints(linkedCustomer.id, {
            points: redeemPoints,
            referenceType: 'BILL',
            referenceId: existingBill?.id || null,
            description: 'Redeemed at POS settlement',
          });
        } catch (err) {
          console.warn('Loyalty redemption failed:', err);
        }
      }

      // Always use createBill - backend automatically detects and settles existing payment_pending bill
      const billPayload = prepareBillPayload();
      const response = await billService.createBill(billPayload);
      const billNumber = existingBill?.billNumber || response?.billNumber || response?.data?.billNumber || receiptNumber;

      toast.dismiss(loadingToast);

      // Handle receipt delivery based on mode
      if (isCounterMode) {
        // Counter Mode: Deliver receipt based on selected method
        const shouldPrint = receiptDeliveryMethod === 'PRINT' || receiptDeliveryMethod === 'BOTH';
        const shouldSendEbill = receiptDeliveryMethod === 'EBILL' || receiptDeliveryMethod === 'BOTH';

        // Print receipt if selected
        if (shouldPrint) {
          toast.loading('Printing receipt...', { id: 'printing' });
          try {
            await handleDirectPrint(billNumber);
            toast.dismiss('printing');
          } catch (printError) {
            toast.dismiss('printing');
            console.warn('Auto-print failed:', printError);
            toast.error('Print failed - try manual print', { duration: 3000 });
          }
        }

        // Send e-Bill via WhatsApp if selected
        // Backend sends it automatically via the WhatsApp gateway when
        // billDeliveryMethod=WHATSAPP and recipientPhone is set in the payload
        if (shouldSendEbill && whatsappNumber) {
          toast.success(`e-Bill sent to +91 ${whatsappNumber}`, { icon: '✅', duration: 3000 });
        }

        toast.success(`Payment settled! Bill #${billNumber}`, { icon: '✓', duration: 2000 });
      } else {
        // Dine-In Mode: Simple success message (receipt already printed via F8)
        toast.success('Payment settled successfully!', { icon: '✓', duration: 2000 });
      }

      const paymentDetails = {
        mode: paymentMode, amount: finalAmount, receiptNumber: billNumber,
        billId: existingBill?.id || response?.id || response?.data?.id || null,
        billNumber: billNumber,
        ...(paymentMode === 'CASH' && { cashReceived, changeDue }),
        ...(paymentMode === 'ONLINE' && { upiAccount: selectedUpiAccount }),
        ...(paymentMode === 'SPLIT' && { cashPart, onlinePart, upiAccount: selectedUpiAccount }),
        discount: { percentage: effectiveDiscountPercentage, amount: discountAmount, reason: discountReason, code: discountCode },
        tax: { percentage: TAX_PERCENTAGE, amount: taxAmount },
        subtotal, totalAmount, roundOff, finalAmount
      };

      // Split bill: mark this check as settled and move to next
      if (allPendingBills.length > 1) {
        const currentBill = allPendingBills[activeCheckIndex];
        const newSettled = { ...settledChecks, [currentBill.id]: true };
        setSettledChecks(newSettled);

        // Check if all checks are settled
        const allSettled = allPendingBills.every(b => newSettled[b.id]);

        if (allSettled) {
          // All checks settled — finalize and close
          toast.success('All split checks settled!', { duration: 3000 });
          finalizeOrder(paymentDetails);
          clearLinkedCustomerAndWaiter(currentTableId, tableSplit);
          onClose();
          if (isCounterMode && onNewOrder) onNewOrder();
          else navigate('/pos/tables');
        } else {
          // Move to next unsettled check
          toast.success(`Check ${currentBill.splitIndex || activeCheckIndex + 1} settled!`, { duration: 2000 });
          const nextUnsettled = allPendingBills.findIndex((b, i) => i > activeCheckIndex && !newSettled[b.id]);
          const nextIdx = nextUnsettled >= 0 ? nextUnsettled : allPendingBills.findIndex(b => !newSettled[b.id]);
          if (nextIdx >= 0) {
            setActiveCheckIndex(nextIdx);
            setExistingBill(allPendingBills[nextIdx]);
            setPaymentMode('CASH');
            setCashReceived(0);
            setCashPart(0);
            setOnlinePart(0);
          }
        }
      } else {
        // Single bill — normal flow
        finalizeOrder(paymentDetails);
        clearLinkedCustomerAndWaiter(currentTableId, tableSplit);
        onClose();

        if (isCounterMode && onNewOrder) onNewOrder();
        else navigate('/pos/tables');
      }

    } catch (error) {
      toast.dismiss(loadingToast);
      const errorMessage = error.response?.data?.message || error.message || 'Payment failed';
      toast.error(errorMessage, { duration: 4000 });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler to open discount modal with lazy loading
  const handleOpenDiscountModal = () => {
    setShowDiscountModal(true);
    // Lazy load: Fetch suggestions only when modal opens
    const itemIds = [...new Set(allKOTItems.map(item => item.menuItemId).filter(Boolean))];
    fetchSuggestions({
      orderType: 'counter',
      subtotal,
      itemIds,
      categoryIds: [],
      customerId: null,
    });
  };

  // Quick discount presets for manual mode
  const discountPresets = [5, 10, 15, 20];

  // Validation for buttons
  const isPaymentValid = () => {
    if (ordersLoading || isProcessing || loadingBill) return false;
    // NC bill with zero amount - no payment needed
    if (isNcBill && finalAmount === 0) return true;
    if (paymentMode === 'CASH' && cashReceived < payableAmount) return false;
    if (paymentMode === 'ONLINE' && !selectedUpiAccount) return false;
    if (paymentMode === 'SPLIT' && (cashPart + onlinePart !== payableAmount || !selectedUpiAccount)) return false;
    // Counter mode: Validate WhatsApp number if e-bill delivery is selected
    if (isCounterMode && (receiptDeliveryMethod === 'EBILL' || receiptDeliveryMethod === 'BOTH')) {
      if (!whatsappNumber || whatsappNumber.length !== 10) return false;
    }
    return true;
  };

  if (!currentOrder) return null;

  // Show loading state while fetching existing bill
  if (loadingBill || ordersLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-full border-4 border-teal-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-teal-600 border-t-transparent animate-spin"></div>
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Loading Bill Details</h3>
          <p className="text-sm text-slate-500">Fetching payment information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-5 py-3 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <CreditCard size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold">
                Settle Payment
                {allPendingBills.length > 1 && (
                  <span className="text-sm font-normal text-teal-200 ml-2">
                    Check {(existingBill?.splitIndex || activeCheckIndex + 1)} of {allPendingBills.length}
                  </span>
                )}
              </h2>
              <p className="text-xs text-teal-100">{displayTableName} • {totalItemsCount} items</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
            <X size={20} />
          </button>
        </div>

        {/* Split Bill Check Tabs */}
        {allPendingBills.length > 1 && (
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex gap-2 overflow-x-auto flex-shrink-0">
            {allPendingBills.map((bill, idx) => {
              const isSettled = settledChecks[bill.id];
              const isActive = activeCheckIndex === idx;
              return (
                <button
                  key={bill.id}
                  onClick={() => {
                    if (!isSettled) {
                      setActiveCheckIndex(idx);
                      setExistingBill(bill);
                      setPaymentMode('CASH');
                      setCashReceived(0);
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                    isSettled
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : isActive
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {isSettled && <Check size={14} />}
                  <span>Check {bill.splitIndex || idx + 1}</span>
                  <span className={`text-xs font-bold ${isSettled ? 'text-green-600' : isActive ? 'text-teal-200' : 'text-slate-400'}`}>
                    ₹{bill.finalAmount || bill.totalAmount || 0}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* NC Bill Banner */}
        {isNcBill && (
          <div className="bg-amber-500 text-white px-5 py-2 flex items-center gap-2 text-sm font-semibold flex-shrink-0">
            <span className="text-lg">⚠</span>
            {`NON-CHARGEABLE BILL${ncReason ? ` — ${NC_REASONS.find(r => r.code === ncReason)?.label || ncReason}` : ''}`}
            {ncAmount > 0 && (
              <span className="ml-auto bg-white/20 px-2 py-0.5 rounded text-xs">
                NC Value: ₹{ncAmount.toFixed(0)}
              </span>
            )}
          </div>
        )}

        {/* Main Content - Split View */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* Left Side - Bill Items (like BillSummary) */}
           <div className="w-1/2 bg-slate-50 border-r border-slate-200 flex flex-col min-h-0">
            {/* Items Header */}
            <div className="px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingBag size={18} className="text-teal-600" />
                <span className="font-semibold text-slate-800">Order Items</span>
                <span className="ml-auto text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                  {totalItemsCount} items
                </span>
                {isNcBill && (
                  <span className="ml-2 text-xs px-3 py-1.5 rounded-lg font-bold border-2 bg-amber-500 text-white border-amber-500">
                    NC Bill
                  </span>
                )}
              </div>
              {isNcBill && ncReason && (
                <div className="mt-1.5 text-xs text-amber-600 font-medium">
                  Reason: {NC_REASONS.find(r => r.code === ncReason)?.label || ncReason}
                </div>
              )}
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-3">
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="bg-slate-100 border-b border-slate-200 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600">
                  <div className="col-span-6">Item</div>
                  <div className="col-span-2 text-center">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-2 text-right">Amt</div>
                </div>

                {/* Items */}
                <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
                  {billItems.map((item, idx) => {
                    const itemNc = isNcBill || item.isComp;
                    return (
                      <div key={idx} className={`px-3 py-2 grid grid-cols-12 gap-2 text-sm hover:bg-slate-50 ${itemNc ? 'bg-amber-50' : ''}`}>
                        <div className={`col-span-6 font-medium truncate flex items-center gap-1.5 ${itemNc ? 'text-slate-500' : 'text-slate-900'}`}>
                          {item.name}
                          {itemNc && <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold flex-shrink-0">NC</span>}
                        </div>
                        <div className={`col-span-2 text-center ${itemNc ? 'text-slate-400' : 'text-slate-600'}`}>{item.quantity}</div>
                        <div className={`col-span-2 text-right ${itemNc ? 'text-slate-400' : 'text-slate-500'}`}>₹{item.price.toFixed(0)}</div>
                        <div className={`col-span-2 text-right font-semibold ${itemNc ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          ₹{item.total.toFixed(0)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Bill Summary Footer */}
            <div className="bg-white border-t border-slate-200 p-4 space-y-2">
              <div className="flex justify-between text-sm text-slate-600">
                <span>{ncAmount > 0 ? 'Gross Total' : 'Subtotal'}</span>
                <span className="font-medium">₹{subtotal.toFixed(2)}</span>
              </div>
              {ncAmount > 0 && (
                <div className="flex justify-between text-sm text-amber-600">
                  <span className="flex items-center gap-1">
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">NC</span>
                    Non-Chargeable
                  </span>
                  <span className="font-medium">- ₹{ncAmount.toFixed(2)}</span>
                </div>
              )}
              {ncAmount > 0 && (
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Chargeable Subtotal</span>
                  <span className="font-medium">₹{chargeableSubtotal.toFixed(2)}</span>
                </div>
              )}
              {discountAmount > 0 && ncAmount <= 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2 space-y-1">
                  <div className="flex justify-between text-sm text-green-700">
                    <span className="flex items-center gap-1.5 font-medium">
                      <Tag size={14} className="text-green-600" />
                      Discount Applied
                      {discountCode && (
                        <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded font-bold">
                          {discountCode}
                        </span>
                      )}
                    </span>
                    <span className="font-bold text-green-700">- ₹{discountAmount.toFixed(2)}</span>
                  </div>
                  {(discountReason || effectiveDiscountPercentage > 0) && (
                    <div className="flex justify-between text-xs text-green-600">
                      <span>{discountReason || (discountCode ? `Promo: ${discountCode}` : 'Manual discount')}</span>
                      {effectiveDiscountPercentage > 0 && (
                        <span className="font-medium">{effectiveDiscountPercentage.toFixed(1)}% off</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {(CGST_RATE > 0 || SGST_RATE > 0) && (
                <>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Taxable Amount</span>
                    <span className="font-medium">₹{taxableAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>CGST ({CGST_RATE.toFixed(1)}%)</span>
                    <span className="font-medium">₹{cgstAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>SGST ({SGST_RATE.toFixed(1)}%)</span>
                    <span className="font-medium">₹{sgstAmount.toFixed(2)}</span>
                  </div>
                  {IS_INCLUSIVE_TAX && (
                    <div className="text-[10px] text-slate-500 italic text-center">
                      (Tax included in price)
                    </div>
                  )}
                </>
              )}
              {roundOff !== 0 && (
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Round Off</span>
                  <span>{roundOff >= 0 ? '+' : ''}₹{roundOff.toFixed(2)}</span>
                </div>
              )}
              {/* Loyalty Redeemed Line */}
              {redeemApplied && loyaltyDiscountAmount > 0 && (
                <div className="flex justify-between text-xs text-amber-700">
                  <span>🎯 Loyalty Redeemed ({redeemPoints} pts)</span>
                  <span className="font-semibold">-₹{loyaltyDiscountAmount.toFixed(0)}</span>
                </div>
              )}
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                {isNcBill && finalAmount === 0 ? (
                  <>
                    <span className="font-bold text-amber-700">No Charge Bill</span>
                    <span className="text-2xl font-black text-amber-600">₹0</span>
                  </>
                ) : (
                  <>
                    <span className="font-bold text-slate-900">Total Payable</span>
                    <span className="text-2xl font-black text-teal-700">₹{payableAmount.toFixed(0)}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Payment Options */}
          <div className="w-1/2 bg-white flex flex-col overflow-y-auto">
            <div className="p-4 space-y-3">

              {/* NC Bill - No Payment Required */}
              {isNcBill && finalAmount === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-3xl">🎁</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">No Payment Required</h3>
                  <p className="text-sm text-slate-500 text-center px-4">
                    This is a Non-Chargeable bill. No payment will be collected.
                  </p>
                  {ncReason && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                      <p className="text-xs text-amber-700 font-medium">
                        Reason: {NC_REASONS.find(r => r.code === ncReason)?.label || ncReason}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
              <>
              {/* Loyalty Points — Link + Applied Badge */}
              {loyaltyBalance && loyaltyBalance.availablePoints > 0 && !isNcBill && (
                redeemApplied ? (
                  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-600">🎯</span>
                      <span className="text-sm text-amber-800 font-medium">{redeemPoints} pts redeemed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-amber-800 font-bold">-₹{loyaltyDiscountAmount.toFixed(0)}</span>
                      <button
                        onClick={() => { setRedeemApplied(false); setRedeemPoints(0); }}
                        className="text-[10px] text-red-500 underline font-medium"
                      >Remove</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowRedeemModal(true)}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 rounded-lg transition mb-2"
                  >
                    <span>🎯</span>
                    <span className="underline font-medium">Redeem Loyalty Points</span>
                    <span className="text-xs text-amber-500">({loyaltyBalance.availablePoints} pts)</span>
                  </button>
                )
              )}

              {/* Payment Mode Selection - Classic Badges */}
              <div className="flex gap-2">
                {[
                  { mode: 'CASH', label: 'Cash' },
                  { mode: 'ONLINE', label: 'UPI' },
                  { mode: 'SPLIT', label: 'Split' },
                ].map((btn) => (
                  <button
                    key={btn.mode}
                    onClick={() => setPaymentMode(btn.mode)}
                    className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition ${
                      paymentMode === btn.mode
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>

              {/* CASH Mode */}
              {paymentMode === 'CASH' && (
                <div className="space-y-3 pt-1">
                  {/* Quick Amount Badges - Colored */}
                  <div className="grid grid-cols-4 gap-1.5">
                    <button
                      onClick={() => setCashReceived(finalAmount)}
                      className={`py-1.5 text-xs font-bold rounded-lg border transition ${
                        cashReceived === finalAmount
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-teal-50 text-teal-700 border-teal-300 hover:bg-teal-100'
                      }`}
                    >
                      Exact
                    </button>
                    {[
                      { value: 100, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', hover: 'hover:bg-amber-100' },
                      { value: 200, bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300', hover: 'hover:bg-orange-100' },
                      { value: 500, bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300', hover: 'hover:bg-green-100' },
                      { value: 1000, bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-300', hover: 'hover:bg-pink-100' },
                      { value: 1500, bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', hover: 'hover:bg-blue-100' },
                      { value: 2000, bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300', hover: 'hover:bg-purple-100' },
                      { value: 2500, bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-300', hover: 'hover:bg-indigo-100' },
                    ].map((note) => (
                      <button
                        key={note.value}
                        onClick={() => setCashReceived(note.value)}
                        className={`py-1.5 text-xs font-bold rounded-lg border transition ${
                          cashReceived === note.value
                            ? 'bg-teal-600 text-white border-teal-600'
                            : `${note.bg} ${note.text} ${note.border} ${note.hover}`
                        }`}
                      >
                        ₹{note.value}
                      </button>
                    ))}
                  </div>

                  {/* Cash Input */}
                  <div>
                    <label className="text-[11px] text-slate-500 mb-1 block">Cash Received</label>
                    <input
                      type="number"
                      value={cashReceived || ''}
                      onChange={(e) => setCashReceived(parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="w-full text-xl font-semibold text-center py-2.5 px-3 rounded-lg bg-slate-50 border border-slate-300 focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                    />
                  </div>

                  {/* Change Due */}
                  {cashReceived >= finalAmount && (
                    <div className="flex justify-between items-center py-2 px-3 bg-amber-50 rounded-lg">
                      <span className="text-sm text-amber-700">Change</span>
                      <span className="text-lg font-bold text-amber-700">₹{changeDue.toFixed(0)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ONLINE Mode */}
              {paymentMode === 'ONLINE' && (
                <div className="space-y-2 pt-1">
                  {accountsLoading ? (
                    <p className="text-sm text-slate-500 py-4 text-center">Loading...</p>
                  ) : upiAccounts.length === 0 ? (
                    <p className="text-sm text-red-500 py-4 text-center">No UPI accounts</p>
                  ) : (
                    <div className="space-y-3">
                      {upiAccounts.map((account) => (
                        <button
                          key={account.id}
                          onClick={() => setSelectedUpiAccountId(account.id)}
                          className={`w-full text-left p-2.5 rounded-lg border transition relative ${
                            selectedUpiAccountId === account.id
                              ? 'bg-teal-50 border-teal-500 shadow-sm'
                              : 'bg-white border-slate-200 hover:border-teal-300 hover:bg-slate-50'
                          }`}
                        >
                          {/* Today's Collection Badge */}
                          <div className="absolute -top-2 right-2 px-2 py-0.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-[10px] font-bold rounded-full shadow-sm">
                            Today: ₹{Number(account.todays_collection || 0).toLocaleString('en-IN')}
                          </div>

                          <div className="flex items-center gap-2">
                            <Landmark
                              size={16}
                              className={selectedUpiAccountId === account.id ? 'text-teal-600' : 'text-slate-400'}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-xs text-slate-900 truncate">
                                {account.bank_name || account.account_name}
                              </p>
                              <p className="text-xs text-slate-500 truncate">
                                {account.upi_id} {account.payment_gateway && `• ${account.payment_gateway}`}
                              </p>
                            </div>
                            {selectedUpiAccountId === account.id && (
                              <CheckCircle2 size={14} className="text-teal-600 flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* SPLIT Mode */}
              {paymentMode === 'SPLIT' && (
                <div className="space-y-3 pt-1">
                  {/* Quick Split Buttons */}
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => { setCashPart(Math.round(finalAmount / 2)); setOnlinePart(Math.round(finalAmount / 2)); }}
                      className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-violet-50 text-violet-700 border border-violet-300 hover:bg-violet-100 transition"
                    >
                      50-50
                    </button>
                    <button
                      onClick={() => { setCashPart(Math.round(finalAmount * 0.25)); setOnlinePart(Math.round(finalAmount * 0.75)); }}
                      className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100 transition"
                    >
                      25-75
                    </button>
                    <button
                      onClick={() => { setCashPart(Math.round(finalAmount * 0.75)); setOnlinePart(Math.round(finalAmount * 0.25)); }}
                      className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 transition"
                    >
                      75-25
                    </button>
                    <button
                      onClick={() => { setCashPart(0); setOnlinePart(0); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200 transition"
                    >
                      Clear
                    </button>
                  </div>

                  {/* Amount Inputs */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] text-slate-500 mb-1 block">Cash Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                        <input
                          type="number"
                          value={cashPart || ''}
                          onChange={(e) => setCashPart(parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="w-full text-lg font-semibold text-center py-2 pl-7 pr-2 rounded-lg bg-slate-50 border border-slate-300 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                        />
                      </div>
                      {/* Auto-fill remaining to Cash */}
                      {cashPart === 0 && onlinePart > 0 && onlinePart < finalAmount && (
                        <button
                          onClick={() => setCashPart(finalAmount - onlinePart)}
                          className="mt-1 text-[10px] text-teal-600 hover:text-teal-700"
                        >
                          + Fill ₹{finalAmount - onlinePart}
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500 mb-1 block">UPI Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                        <input
                          type="number"
                          value={onlinePart || ''}
                          onChange={(e) => setOnlinePart(parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="w-full text-lg font-semibold text-center py-2 pl-7 pr-2 rounded-lg bg-slate-50 border border-slate-300 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                        />
                      </div>
                      {/* Auto-fill remaining to UPI */}
                      {onlinePart === 0 && cashPart > 0 && cashPart < finalAmount && (
                        <button
                          onClick={() => setOnlinePart(finalAmount - cashPart)}
                          className="mt-1 text-[10px] text-teal-600 hover:text-teal-700"
                        >
                          + Fill ₹{finalAmount - cashPart}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Split Status */}
                  <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                    cashPart + onlinePart === finalAmount
                      ? 'bg-green-50 border border-green-200'
                      : cashPart + onlinePart > finalAmount
                      ? 'bg-red-50 border border-red-200'
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <span className={`text-xs font-medium ${
                      cashPart + onlinePart === finalAmount
                        ? 'text-green-700'
                        : cashPart + onlinePart > finalAmount
                        ? 'text-red-700'
                        : 'text-amber-700'
                    }`}>
                      {cashPart + onlinePart === finalAmount
                        ? '✓ Payment complete'
                        : cashPart + onlinePart > finalAmount
                        ? 'Excess amount'
                        : 'Remaining'
                      }
                    </span>
                    <span className={`text-sm font-bold ${
                      cashPart + onlinePart === finalAmount
                        ? 'text-green-700'
                        : cashPart + onlinePart > finalAmount
                        ? 'text-red-700'
                        : 'text-amber-700'
                    }`}>
                      {cashPart + onlinePart === finalAmount
                        ? `₹${cashPart} + ₹${onlinePart}`
                        : cashPart + onlinePart > finalAmount
                        ? `+₹${(cashPart + onlinePart - finalAmount).toFixed(0)}`
                        : `₹${(finalAmount - (cashPart + onlinePart)).toFixed(0)}`
                      }
                    </span>
                  </div>

                  {/* UPI Account for Split */}
                  {onlinePart > 0 && upiAccounts.length > 0 && (
                    <div className="pt-2 border-t border-slate-100">
                      <label className="text-[11px] text-slate-500 mb-2 block">Select UPI Account</label>
                      <div className="space-y-2.5">
                        {upiAccounts.map((account) => (
                          <button
                            key={account.id}
                            onClick={() => setSelectedUpiAccountId(account.id)}
                            className={`w-full text-left p-2.5 rounded-lg border transition relative ${
                              selectedUpiAccountId === account.id
                                ? 'bg-teal-50 border-teal-500 shadow-sm'
                                : 'bg-white border-slate-200 hover:border-teal-300 hover:bg-slate-50'
                            }`}
                          >
                            {/* Today's Collection Badge */}
                            <div className="absolute -top-2 right-2 px-2 py-0.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-[10px] font-bold rounded-full shadow-sm">
                              Today: ₹{Number(account.todays_collection || 0).toLocaleString('en-IN')}
                            </div>

                            <div className="flex items-center gap-2">
                              <Landmark
                                size={16}
                                className={selectedUpiAccountId === account.id ? 'text-teal-600' : 'text-slate-400'}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-xs text-slate-900 truncate">
                                  {account.bank_name || account.account_name}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                  {account.upi_id} {account.payment_gateway && `• ${account.payment_gateway}`}
                                </p>
                              </div>
                              {selectedUpiAccountId === account.id && (
                                <CheckCircle2 size={14} className="text-teal-600 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Counter Mode: Discount Section (only when no discount already applied) */}
{isCounterMode && !hasExistingDiscount && (canApplyPromo || canApplyDiscount) && (
  <div className="pt-3 border-t border-slate-200">
    {/* Header with Mode Toggle */}
    <div className="flex items-center justify-between mb-2">
      <label className="text-[11px] text-slate-500 font-medium flex items-center gap-1">
        <BadgePercent size={12} className="text-teal-600" />
        Apply Discount
      </label>
      {/* Only show toggle if BOTH permissions exist */}
      {canApplyPromo && canApplyDiscount && (
        <div className="flex bg-white rounded border border-teal-300 overflow-hidden text-[10px]">
          <button
            onClick={() => { setDiscountMode('code'); removeDiscount(); setDiscountPercentage(0); setDiscountFixedAmount(0); }}
            className={`px-2 py-1 font-medium transition ${
              discountMode === 'code' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-teal-50'
            }`}
          >
            Promo Code
          </button>
          <button
            onClick={() => { setDiscountMode('manual'); removeDiscount(); }}
            className={`px-2 py-1 font-medium transition ${
              discountMode === 'manual' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-teal-50'
            }`}
          >
            Manual
          </button>
        </div>
      )}
      {/* Static label when only one permission */}
      {canApplyPromo && !canApplyDiscount && (
        <span className="text-[10px] text-teal-600 font-medium">Promo Code</span>
      )}
      {!canApplyPromo && canApplyDiscount && (
        <span className="text-[10px] text-teal-600 font-medium">Manual</span>
      )}
    </div>

    <div className="space-y-2">
      {/* Code-Based Discount Mode — only if canApplyPromo */}
      {(discountMode === 'code' || (!canApplyDiscount && canApplyPromo)) && canApplyPromo && (
        <>
          {appliedDiscount ? (
            <AppliedDiscountBadge
              discount={appliedDiscount}
              discountAmount={discountAmount}
              onRemove={removeDiscount}
            />
          ) : (
            <button
              onClick={handleOpenDiscountModal}
              className="w-full flex items-center justify-between p-3 bg-white border-2 border-dashed border-teal-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all group"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition">
                  <Tag size={16} className="text-teal-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-slate-700">Apply Promo Code</p>
                  <p className="text-xs text-slate-500">Browse available offers</p>
                </div>
              </div>
              <ChevronDown size={18} className="text-teal-500" />
            </button>
          )}
        </>
      )}

      {/* Manual Discount Mode — only if canApplyDiscount */}
      {(discountMode === 'manual' || (!canApplyPromo && canApplyDiscount)) && canApplyDiscount && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex bg-white rounded border border-teal-300 overflow-hidden flex-shrink-0">
              <button
                onClick={() => setDiscountType('PERCENTAGE')}
                className={`px-2 py-1.5 transition ${
                  discountType === 'PERCENTAGE' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-teal-100'
                }`}
              >
                <Percent size={14} />
              </button>
              <button
                onClick={() => setDiscountType('FIXED')}
                className={`px-2 py-1.5 transition ${
                  discountType === 'FIXED' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-teal-100'
                }`}
              >
                <IndianRupee size={14} />
              </button>
            </div>
            <div className="relative flex-1">
              {discountType === 'PERCENTAGE' ? (
                <>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={discountPercentage || ''}
                    onChange={(e) => setDiscountPercentage(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                    placeholder="0"
                    className="w-full px-2 py-1.5 pr-6 border border-teal-300 rounded text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent bg-white"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-teal-600 font-bold text-xs">%</span>
                </>
              ) : (
                <>
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-teal-600 font-bold text-xs">₹</span>
                  <input
                    type="number"
                    min="0"
                    max={subtotal}
                    value={discountFixedAmount || ''}
                    onChange={(e) => setDiscountFixedAmount(Math.min(subtotal, Math.max(0, parseFloat(e.target.value) || 0)))}
                    placeholder="0"
                    className="w-full px-2 py-1.5 pl-5 border border-teal-300 rounded text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent bg-white"
                  />
                </>
              )}
            </div>
            {discountType === 'PERCENTAGE' && (
              <div className="flex gap-0.5 flex-shrink-0">
                {discountPresets.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setDiscountPercentage(preset)}
                    className={`px-1.5 py-1 text-[10px] font-bold rounded transition ${
                      discountPercentage === preset
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-teal-700 border border-teal-300 hover:bg-teal-100'
                    }`}
                  >
                    {preset}%
                  </button>
                ))}
              </div>
            )}
          </div>
          {discountAmount > 0 && (
            <div className="flex items-center justify-between bg-amber-100 px-2 py-1.5 rounded border border-amber-300">
              <span className="text-[10px] font-semibold text-amber-700">Manual Discount</span>
              <span className="text-xs font-bold text-amber-700">- ₹{discountAmount.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
)}

              {/* Counter Mode: Receipt Delivery Options */}
              {isCounterMode && (
                <div className="pt-3 border-t border-slate-200 space-y-3">
                  <label className="text-[11px] text-slate-500 font-medium block">Receipt Delivery</label>

                  {/* Delivery Method Toggle */}
                  <div className="flex gap-2">
                    {[
                      { mode: 'PRINT', label: 'Print', icon: Printer },
                      { mode: 'EBILL', label: 'e-Bill', icon: Smartphone },
                      { mode: 'BOTH', label: 'Both', icon: Receipt },
                    ].map((btn) => {
                      const Icon = btn.icon;
                      return (
                        <button
                          key={btn.mode}
                          onClick={() => setReceiptDeliveryMethod(btn.mode)}
                          className={`flex-1 py-2 text-xs font-semibold rounded-lg transition flex items-center justify-center gap-1.5 ${
                            receiptDeliveryMethod === btn.mode
                              ? btn.mode === 'EBILL' || btn.mode === 'BOTH'
                                ? 'bg-green-600 text-white shadow-sm'
                                : 'bg-purple-600 text-white shadow-sm'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          <Icon size={14} />
                          {btn.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* WhatsApp Number Input - Show when e-Bill or Both is selected */}
                  {(receiptDeliveryMethod === 'EBILL' || receiptDeliveryMethod === 'BOTH') && (
                    <div className="bg-green-50 rounded-lg border border-green-200 overflow-hidden">
                      <div className="flex items-center gap-3 px-3 py-2">
                        {/* Customer Badge (if linked) */}
                        {linkedCustomer && customerName && (
                          <div className="flex items-center gap-2 px-2 py-1 bg-green-100 rounded-full">
                            <User size={12} className="text-green-600" />
                            <span className="text-xs font-medium text-green-800">{customerName}</span>
                          </div>
                        )}

                        {/* WhatsApp Input - Inline */}
                        <div className="flex-1 flex items-center">
                          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-l border border-r-0 text-sm font-semibold ${
                            whatsappNumber.length === 10 ? 'border-green-400 bg-green-100 text-green-800' : 'border-green-300 bg-green-100 text-green-800'
                          }`}>
                            <Phone size={13} />
                            <span>+91</span>
                          </div>
                          <input
                            type="tel"
                            value={whatsappNumber}
                            onChange={(e) => setWhatsappNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            placeholder="10-digit mobile number"
                            maxLength={10}
                            className={`flex-1 px-2.5 py-1.5 border rounded-r text-sm font-medium focus:outline-none focus:ring-1 focus:ring-green-500 ${
                              whatsappNumber.length === 10
                                ? 'border-green-400 bg-green-100'
                                : 'border-green-300 bg-white'
                            }`}
                          />
                          {whatsappNumber.length === 10 && (
                            <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                      {whatsappNumber.length > 0 && whatsappNumber.length < 10 && (
                        <div className="px-3 pb-2">
                          <p className="text-amber-600 text-xs">Enter complete 10-digit number</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Action Buttons */}
        <div className="bg-slate-100 border-t-2 border-teal-600 p-4 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition"
          >
            Cancel
          </button>

          <button
            onClick={() => setShowConfirm(true)}
            disabled={!isPaymentValid()}
            className={`px-8 py-2.5 rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg ${
              !isPaymentValid()
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : isNcBill && finalAmount === 0
                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                  : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            <CheckCircle2 size={18} />
            {isNcBill && finalAmount === 0 ? 'Close NC Bill' : 'Settle Payment'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center space-y-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${isNcBill ? 'bg-amber-100' : 'bg-teal-100'}`}>
              {isNcBill ? <span className="text-2xl">🎁</span> : <CheckCircle2 size={32} className="text-teal-600" />}
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              {isNcBill && finalAmount === 0 ? 'Confirm NC Bill' : 'Confirm Settlement'}
            </h3>
            <p className="text-sm text-slate-600">
              {isNcBill && finalAmount === 0
                ? <>Close this bill as <span className="font-bold text-amber-600">Non-Chargeable</span>?{ncReason && <><br/><span className="text-xs text-amber-500">Reason: {NC_REASONS.find(r => r.code === ncReason)?.label || ncReason}</span></>}</>
                : <>Settle payment of <span className="font-bold text-teal-700">₹{finalAmount}</span> via {paymentMode === 'CASH' ? 'Cash' : paymentMode === 'ONLINE' ? 'UPI' : 'Split'}?</>
              }
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowConfirm(false); handleSettlePayment(); }}
                disabled={isProcessing}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition"
              >
                {isProcessing ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Selection Modal (Counter Mode only) */}
      {canApplyPromo && (
        <DiscountSelectionModal
          isOpen={showDiscountModal}
          onClose={() => setShowDiscountModal(false)}
          suggestions={discountSuggestions}
          loading={discountsLoading}
          onSelectDiscount={(discount) => {
            applyDiscount(discount);
            toast.success(`${discount.code} applied!`);
          }}
          onValidateCode={async (code) => {
            return await validateCode(code, {
              orderType: 'counter',
              subtotal,
              itemIds: [...new Set(allKOTItems.map(item => item.menuItemId).filter(Boolean))],
              categoryIds: [],
              customerId: null,
            });
          }}
          validating={codeValidating}
          calculateSaving={(discount) => {
            if (discount.eligible === false) return 0;
            const cartItems = allKOTItems.map(item => ({
              menuItemId: item.menuItemId,
              categoryId: item.categoryId,
              price: item.finalPrice || item.price,
              quantity: item.quantity,
            }));
            return calculateDiscountAmount(discount, cartItems, subtotal);
          }}
        />
      )}

      {/* Loyalty Redeem Modal */}
      {showRedeemModal && loyaltyBalance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xs overflow-hidden">
            <div className="bg-amber-500 text-white px-5 py-3">
              <h3 className="text-sm font-bold">Redeem Loyalty Points</h3>
              <p className="text-xs text-amber-100">{loyaltyBalance.availablePoints} pts available (₹{loyaltyBalance.redeemValue} value)</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Points to Redeem</label>
                <input
                  type="number"
                  min={loyaltyBalance.minRedeemPoints || 1}
                  max={Math.min(loyaltyBalance.availablePoints, Math.floor(finalAmount / (loyaltyBalance.redeemRate || 1)))}
                  value={redeemPoints || ''}
                  onChange={(e) => setRedeemPoints(parseInt(e.target.value) || 0)}
                  placeholder={`Min ${loyaltyBalance.minRedeemPoints || 1} pts`}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  autoFocus
                />
              </div>
              {redeemPoints > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center">
                  <p className="text-xs text-amber-600">Discount</p>
                  <p className="text-2xl font-black text-amber-800">₹{Math.round(redeemPoints * (loyaltyBalance.redeemRate || 1))}</p>
                  <p className="text-[10px] text-amber-500 mt-1">
                    Payable: ₹{finalAmount} → ₹{Math.max(0, finalAmount - Math.round(redeemPoints * (loyaltyBalance.redeemRate || 1)))}
                  </p>
                </div>
              )}
              {loyaltyBalance.minRedeemPoints > 0 && (
                <p className="text-[10px] text-slate-400">Minimum {loyaltyBalance.minRedeemPoints} points required</p>
              )}
            </div>
            <div className="border-t border-slate-200 p-4 flex gap-2">
              <button
                onClick={() => { setShowRedeemModal(false); setRedeemPoints(0); }}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition"
              >Cancel</button>
              <button
                onClick={() => {
                  if (redeemPoints >= (loyaltyBalance.minRedeemPoints || 1) && redeemPoints <= loyaltyBalance.availablePoints) {
                    setRedeemApplied(true);
                    setShowRedeemModal(false);
                    toast.success(`${redeemPoints} points applied — ₹${Math.round(redeemPoints * (loyaltyBalance.redeemRate || 1))} discount`);
                  } else {
                    toast.error(`Enter ${loyaltyBalance.minRedeemPoints || 1} to ${loyaltyBalance.availablePoints} points`);
                  }
                }}
                disabled={redeemPoints < (loyaltyBalance.minRedeemPoints || 1)}
                className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold transition disabled:opacity-50"
              >Apply Points</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
